/** Formattazione italiana: euro, date, nomi dei mesi. */

const euro = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: 'always',
});

const euroCompact = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  useGrouping: 'always',
});

export function formatEuro(value: number, compact = false): string {
  return (compact ? euroCompact : euro).format(value);
}

/** Numero senza simbolo, per i campi di input. */
export function formatAmount(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

export const MONTH_NAMES = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
];

export function monthName(month: number): string {
  return MONTH_NAMES[month - 1] ?? '';
}

export function monthLabel(year: number, month: number): string {
  return `${monthName(month)} ${year}`;
}

/** "2026-09-06" -> Date in UTC, senza slittamenti di fuso. */
export function parseIsoDate(iso: string): Date {
  return new Date(`${iso}T00:00:00.000Z`);
}

export function toIsoDate(date: Date): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

export function todayIso(): string {
  return toIsoDate(new Date());
}

/** Intestazioni della timeline: Oggi, Ieri, poi la data estesa. */
export function dayHeading(iso: string): string {
  const today = todayIso();
  if (iso === today) return 'Oggi';

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (iso === toIsoDate(yesterday)) return 'Ieri';

  const d = parseIsoDate(iso);
  const weekday = new Intl.DateTimeFormat('it-IT', { weekday: 'long', timeZone: 'UTC' }).format(d);
  const rest = new Intl.DateTimeFormat('it-IT', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(d);
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} ${rest}`;
}

export function shortDate(iso: string): string {
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parseIsoDate(iso));
}

export function percent(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(value);
}

/** "11 ott": l'anno compare solo se non e' quello in corso. */
export function dayMonth(iso: string): string {
  const d = parseIsoDate(iso);
  const sameYear = d.getUTCFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat('it-IT', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  })
    .format(d)
    .replace('.', '');
}

/** "10 ott 2026": per i saldi, dove la data e' parte del dato. */
export function dayMonthYear(iso: string): string {
  return new Intl.DateTimeFormat('it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
    .format(parseIsoDate(iso))
    .replace('.', '');
}
